import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, 
  Clock, 
  Calendar, 
  Users, 
  BookOpen, 
  Award, 
  CheckCircle, 
  ArrowRight, 
  Monitor, 
  Building, 
  Globe,
  Star,
  FileText,
  Download,
  Phone,
  Sparkles,
  TrendingUp,
  Target,
  Zap,
  PlayCircle,
  ChevronDown,
  MapPin,
  Mail
} from 'lucide-react';

const Admissions = () => {
  const [activeTab, setActiveTab] = useState('fulltime');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      title: "Shape Your Tomorrow",
      subtitle: "With World-Class Technical Education",
      description: "Join 15,000+ successful graduates who started their journey at Federal Polytechnic Bida",
      image: "🎓",
      color: "from-blue-600 via-purple-600 to-indigo-700"
    },
    {
      title: "Learn Your Way",
      subtitle: "Flexible Programs for Every Lifestyle",
      description: "Full-time, Evening, Weekend, or Distance Learning - Choose what works for you",
      image: "⏰",
      color: "from-emerald-500 via-teal-600 to-cyan-700"
    },
    {
      title: "Industry Ready",
      subtitle: "95% Graduate Employment Rate",
      description: "Our programs are designed with industry partners to ensure you're job-ready",
      image: "🚀",
      color: "from-orange-500 via-red-500 to-pink-600"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const programs = {
    fulltime: {
      title: "Full-Time Programmes",
      icon: GraduationCap,
      color: "from-blue-600 to-indigo-600",
      badge: "Most Popular",
      description: "Traditional on-campus learning with full academic support and campus life experience",
      options: [
        {
          name: "National Diploma (ND)",
          duration: "2 Years",
          level: "Entry Level",
          description: "Foundation technical education program preparing students for industry and further studies",
          features: ["Practical Training", "Industry Exposure", "Modern Facilities", "Expert Faculty"],
          popular: true
        },
        {
          name: "Higher National Diploma (HND)",
          duration: "2 Years",
          level: "Advanced Level", 
          description: "Advanced technical education equivalent to university degree level",
          features: ["Advanced Projects", "Research Component", "Industry Partnership", "Career Ready"]
        }
      ]
    },
    evening: {
      title: "Evening Programmes (DCE)",
      icon: Clock,
      color: "from-purple-600 to-pink-600",
      badge: "For Professionals",
      description: "Flexible evening classes designed for working professionals who want to advance their careers",
      options: [
        {
          name: "National Diploma (ND)",
          duration: "2.5 Years",
          level: "Entry Level",
          description: "Perfect for working professionals seeking technical education without leaving their jobs",
          features: ["Flexible Schedule", "Work-Study Balance", "Same Quality Education", "Evening Support"]
        },
        {
          name: "National Diploma II Special",
          duration: "2 Years",
          level: "Specialized",
          description: "Specialized program with enhanced curriculum and practical focus for career changers",
          features: ["Enhanced Curriculum", "Specialized Skills", "Industry Focus", "Accelerated Learning"]
        },
        {
          name: "Higher National Diploma (HND)",
          duration: "2.5 Years",
          level: "Advanced Level",
          description: "Advanced evening program for significant career advancement and leadership roles",
          features: ["Professional Development", "Leadership Skills", "Advanced Technology", "Network Building"]
        }
      ]
    },
    weekend: {
      title: "Weekend Programmes",
      icon: Calendar,
      color: "from-green-600 to-emerald-600",
      badge: "Maximum Flexibility",
      description: "Weekend-only classes perfect for busy professionals who need ultimate scheduling flexibility",
      options: [
        {
          name: "National Diploma (ND)",
          duration: "3 Years",
          level: "Entry Level",
          description: "Comprehensive weekend program maintaining full academic excellence and standards",
          features: ["Weekend Convenience", "Full Curriculum", "Peer Networking", "Flexible Learning"]
        },
        {
          name: "Higher National Diploma (HND)",
          duration: "3 Years", 
          level: "Advanced Level",
          description: "Advanced weekend program designed for professional growth and career transformation",
          features: ["Strategic Learning", "Professional Growth", "Industry Connections", "Leadership Development"]
        }
      ]
    },
    university: {
      title: "University Affiliations",
      icon: Building,
      color: "from-orange-600 to-red-600",
      badge: "Bachelor's Degree",
      description: "Bachelor's degree programs through prestigious university partnerships for higher academic achievement",
      options: [
        {
          name: "Federal University of Technology, Minna",
          duration: "4 Years",
          level: "Bachelor's Degree",
          description: "Engineering and technology bachelor's programs with cutting-edge research opportunities",
          features: ["University Standard", "Research Focus", "Modern Labs", "Industry Links"]
        },
        {
          name: "Ibrahim Badamasi Babangida University, Lapai",
          duration: "4 Years",
          level: "Bachelor's Degree",
          description: "Comprehensive bachelor's degree programs with diverse academic opportunities",
          features: ["Academic Excellence", "Diverse Programs", "Quality Assurance", "Graduate Opportunities"]
        }
      ]
    },
    distance: {
      title: "Distance Learning (ODFeL)",
      icon: Globe,
      color: "from-teal-600 to-cyan-600",
      badge: "Study Anywhere",
      description: "Open and Distance e-Learning platform offering maximum flexibility for remote learners worldwide",
      options: [
        {
          name: "Online Learning Programs",
          duration: "Flexible Timeline",
          level: "Various Levels",
          description: "Learn from anywhere in the world with our comprehensive online platform and digital resources",
          features: ["Study Anywhere", "Self-Paced", "Digital Resources", "24/7 Online Support"]
        }
      ]
    }
  };

  const admissionSteps = [
    {
      step: 1,
      title: "Explore Programs",
      description: "Discover our diverse range of programs and find the perfect fit for your career goals and lifestyle",
      icon: Target,
      color: "from-blue-500 to-cyan-500"
    },
    {
      step: 2,
      title: "Check Requirements",
      description: "Review entry requirements and ensure you meet all academic and documentation criteria",
      icon: CheckCircle,
      color: "from-green-500 to-emerald-500"
    },
    {
      step: 3,
      title: "Submit Application",
      description: "Complete your online application and upload all required documents through our portal",
      icon: FileText,
      color: "from-purple-500 to-pink-500"
    },
    {
      step: 4,
      title: "Begin Journey",
      description: "Start your transformative academic journey and unlock your potential with us",
      icon: Award,
      color: "from-orange-500 to-red-500"
    }
  ];

  const requirements = [
    "O'Level results (WAEC/NECO/NABTEB) with minimum 5 credits including English and Mathematics",
    "JAMB UTME result with acceptable score (for ND programs)",
    "National Diploma certificate with merit or above (for HND programs)",
    "Valid government-issued identification (National ID, Driver's License, or International Passport)",
    "Recent passport photographs (white background, not older than 3 months)",
    "Birth certificate or sworn age declaration affidavit",
    "Local Government Area certificate of origin or indigene certificate"
  ];

  const stats = [
    { number: "15,000+", label: "Graduates", icon: Users },
    { number: "47", label: "Years Excellence", icon: Star },
    { number: "95%", label: "Employment Rate", icon: TrendingUp },
    { number: "50+", label: "Industry Partners", icon: Building }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Enhanced Hero Section */}
      <div className="relative overflow-hidden min-h-screen flex items-center">
        {/* Dynamic Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${heroSlides[currentSlide].color} transition-all duration-1000`}>
          <div className="absolute inset-0 bg-black/30"></div>
          
          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-white/10 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-white/10 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
          
          {/* Geometric Patterns */}
          <div className="absolute top-32 right-32 w-4 h-4 bg-white/20 transform rotate-45 animate-spin" style={{ animationDuration: '10s' }}></div>
          <div className="absolute bottom-40 left-20 w-6 h-6 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 right-10 w-3 h-3 bg-white/25 transform rotate-45 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20">
                <Sparkles className="w-4 h-4 mr-2" />
                Federal Polytechnic Bida - Excellence Since 1977
              </div>
              
              <h1 className="text-6xl lg:text-8xl font-black text-white mb-6 leading-none">
                {heroSlides[currentSlide].title}
                <span className="block text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent mt-2">
                  {heroSlides[currentSlide].subtitle}
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-2xl leading-relaxed">
                {heroSlides[currentSlide].description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="https://www.youtube.com/@TheFederalPolyBida" target="_blank" rel="noopener noreferrer">
                <button className="group bg-white text-gray-900 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transform hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 flex items-center justify-center">
                  <PlayCircle className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                  Start Your Journey
                </button>
                </a>
                <a href="/images/student.pdf" download>
                  <button className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/30 hover:border-white/50 transition-all duration-300 flex items-center justify-center">
                    <Download className="w-6 h-6 mr-3" />
                    Download Brochure
                  </button>
                </a>
              </div>
              
              {/* Live Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl lg:text-4xl font-black text-white mb-1">{stat.number}</div>
                    <div className="text-white/80 text-sm font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Element */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative">
                <div className="w-96 h-96 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center">
                  <div className="text-9xl">{heroSlides[currentSlide].image}</div>
                </div>
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-white/30 rounded-full flex items-center justify-center animate-pulse">
                  <Zap className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 right-8 animate-bounce">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all duration-300">
              <ChevronDown className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* Programs Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4 mr-2" />
              Academic Programs
            </div>
            <h2 className="text-5xl font-black text-gray-900 mb-6">Choose Your Path to Success</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover flexible learning options designed to fit your lifestyle, career goals, and academic aspirations
            </p>
          </div>

          {/* Enhanced Tab Navigation */}
          <div className="relative mb-12">
            <div className="flex overflow-x-auto pb-4 scrollbar-hide">
              <div className="flex space-x-4 mx-auto">
                {Object.entries(programs).map(([key, program]) => {
                  const IconComponent = program.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`relative flex-shrink-0 group transition-all duration-300 ${
                        activeTab === key ? 'scale-105' : 'hover:scale-102'
                      }`}
                    >
                      <div className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                        activeTab === key
                          ? `bg-gradient-to-r ${program.color} text-white border-transparent shadow-2xl`
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 shadow-lg hover:shadow-xl'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <IconComponent className="w-6 h-6" />
                          <div className="text-left">
                            <div className="font-bold text-sm">{program.title}</div>
                            <div className={`text-xs opacity-80 ${activeTab === key ? 'text-white/80' : 'text-gray-500'}`}>
                              {program.badge}
                            </div>
                          </div>
                        </div>
                      </div>
                      {activeTab === key && (
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full shadow-lg"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Enhanced Program Content */}
          <div className="relative">
            <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl p-8 shadow-2xl border border-gray-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className={`w-20 h-20 rounded-3xl bg-gradient-to-r ${programs[activeTab].color} flex items-center justify-center mr-6 shadow-lg`}>
                    {React.createElement(programs[activeTab].icon, { className: "w-10 h-10 text-white" })}
                  </div>
                  <div>
                    <h3 className="text-3xl font-black text-gray-900">{programs[activeTab].title}</h3>
                    <p className="text-gray-600 text-lg mt-1">{programs[activeTab].description}</p>
                  </div>
                </div>
                <div className={`px-4 py-2 bg-gradient-to-r ${programs[activeTab].color} text-white rounded-full text-sm font-bold`}>
                  {programs[activeTab].badge}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {programs[activeTab].options.map((option, index) => (
                  <div
                    key={index}
                    className={`relative bg-white rounded-2xl p-6 shadow-lg border-2 hover:shadow-2xl transition-all duration-500 cursor-pointer group ${
                      selectedProgram === `${activeTab}-${index}` 
                        ? `border-blue-300 shadow-blue-100 scale-105` 
                        : 'border-gray-100 hover:border-gray-200 hover:scale-102'
                    } ${option.popular ? 'ring-2 ring-blue-200' : ''}`}
                    onClick={() => setSelectedProgram(selectedProgram === `${activeTab}-${index}` ? null : `${activeTab}-${index}`)}
                  >
                    {option.popular && (
                      <div className="absolute -top-3 left-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        Most Popular
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                          {option.name}
                        </h4>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-1" />
                            {option.duration}
                          </span>
                          <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-xs font-bold">
                            {option.level}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className={`w-5 h-5 text-gray-400 group-hover:text-blue-500 transform transition-all duration-300 ${
                        selectedProgram === `${activeTab}-${index}` ? 'rotate-90 text-blue-500' : ''
                      }`} />
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed">{option.description}</p>

                    {selectedProgram === `${activeTab}-${index}` && (
                      <div className="border-t border-gray-200 pt-4 mt-4 animate-in slide-in-from-top duration-300">
                        <h5 className="font-bold text-gray-900 mb-3 flex items-center">
                          <Star className="w-4 h-4 mr-2 text-yellow-500" />
                          Program Highlights
                        </h5>
                        <div className="grid grid-cols-1 gap-3">
                          {option.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                              {feature}
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => window.open("https://portal.fedpolybida.edu.ng/", "_blank")}
                          className={`mt-4 w-full bg-gradient-to-r ${programs[activeTab].color} text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Apply Now
                        </button>

                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Admission Process */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              <Target className="w-4 h-4 mr-2" />
              Admission Process
            </div>
            <h2 className="text-5xl font-black text-gray-900 mb-6">Your Journey Starts Here</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Follow these simple steps to begin your transformative educational experience
            </p>
          </div>

          <div className="relative">
            {/* Connection Lines */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-orange-200 transform -translate-y-1/2 z-0"></div>
            
            <div className="grid lg:grid-cols-4 gap-8 relative z-10">
              {admissionSteps.map((step, index) => (
                <div key={index} className="relative group">
                  <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-gray-100 hover:border-gray-200 h-full">
                    <div className="text-center">
                      <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <step.icon className="w-10 h-10 text-white" />
                      </div>
                      <div className="absolute -top-4 left-4 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {step.step}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Requirements and Info Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {/* Requirements */}
          <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900">Entry Requirements</h3>
                <p className="text-gray-600 text-lg">Everything you need to get started</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {requirements.map((requirement, index) => (
                <div key={index} className="flex items-start p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-700 leading-relaxed">{requirement}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl border border-blue-100">
              <h4 className="font-bold text-blue-900 mb-3 flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                Need Assistance?
              </h4>
              <p className="text-blue-700 mb-4">Our dedicated admissions team is ready to help you through every step of the process.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Admissions
                </button>
                <button className="border border-blue-300 text-blue-600 px-6 py-3 rounded-xl font-bold hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Us
                </button>
              </div>
            </div>
          </div>

          {/* Why Choose Us & Quick Apply */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">Why Federal Polytechnic Bida?</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                    <stat.icon className="w-6 h-6 mx-auto mb-2 text-cyan-300" />
                    <div className="text-2xl font-black mb-1">{stat.number}</div>
                    <div className="text-blue-100 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-8">
                {[
                  "NBTE Fully Accredited Programs",
                  "State-of-the-Art Learning Facilities", 
                  "Industry-Expert Faculty Members",
                  "Strong Alumni Network Worldwide",
                  "97% Student Satisfaction Rate",
                  "Modern Campus Infrastructure"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-cyan-300 mr-3 flex-shrink-0" />
                    <span className="text-blue-100 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Apply */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Apply?</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Take the first step towards your future career in technology and engineering. Join thousands of successful graduates.
                </p>
                <div className="space-y-4">
                  <button
                    onClick={() => window.open("https://portal.fedpolybida.edu.ng/", "_blank")}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-6 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group"
                  >
                    <Sparkles className="w-5 h-5 mr-2 group-hover:animate-spin" />
                    Start Application
                  </button>

                  <div className="text-sm text-gray-500 space-y-2">
                    <div className="flex items-center justify-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Application Deadline: October 31st, 2026
                    </div>
                    <div className="flex items-center justify-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Processing Time: 2-3 weeks
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Student Testimonial */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border border-emerald-100">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  A
                </div>
                <div>
                  <div className="flex items-center mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-4 leading-relaxed">
                    "Federal Polytechnic Bida transformed my career completely. The practical approach to learning and industry connections helped me secure my dream job immediately after graduation. The faculty support is simply outstanding!"
                  </p>
                  <div>
                    <p className="font-bold text-gray-900">Aisha Mohammed</p>
                    <p className="text-sm text-gray-600">Computer Engineering Graduate, Class of 2023</p>
                    <p className="text-xs text-emerald-600 font-medium">Now Software Engineer at TechCorp Nigeria</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Footer */}
        <div className="bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 rounded-3xl p-8 shadow-2xl">
          <div className="grid md:grid-cols-3 gap-8 text-white">
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-cyan-400" />
                Visit Our Campus
              </h3>
              <p className="text-gray-300 mb-2">Federal Polytechnic, Bida</p>
              <p className="text-gray-300 mb-4">Niger State, Nigeria</p>
              <p className="text-sm text-gray-400">Campus tours available Monday - Friday</p>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-cyan-400" />
                Get in Touch
              </h3>
              <p className="text-gray-300 mb-2">+234 123 456 789</p>
              <p className="text-gray-300 mb-4">admissions@fedpolybida.edu.ng</p>
              <p className="text-sm text-gray-400">Monday - Friday: 8:00 AM - 5:00 PM</p>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-cyan-400" />
                Online Resources
              </h3>
              <div className="space-y-2">
                <a href="https://www.youtube.com/@TheFederalPolyBida" className="block text-gray-300 hover:text-white transition-colors duration-300 text-sm">
                  Virtual Campus Tour
                </a>
                <a href="/apply" className="block text-gray-300 hover:text-white transition-colors duration-300 text-sm">
                  Program Catalog
                </a>
                <a href="/portal" className="block text-gray-300 hover:text-white transition-colors duration-300 text-sm">
                  Student Portal
                </a>
                <a href="#" className="block text-gray-300 hover:text-white transition-colors duration-300 text-sm">
                  FAQ Section
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-6 text-center">
            <p className="text-gray-400 text-sm">
              Ready to transform your future? Your journey to success begins with a single step.
            </p>
            <div className="flex justify-center space-x-4 mt-4">
              <button className="bg-white/10 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-white/20 transition-all duration-300">
                Schedule Campus Visit
              </button>
              <button
                onClick={() => window.open("https://portal.fedpolybida.edu.ng/", "_blank")}
                className="bg-cyan-500 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-cyan-600 transition-all duration-300"
              >
                Apply Online Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admissions;